"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { Locale } from "@/i18n-config";
import { getLandingAddOnCopy } from "@/content/landing-add-on-copy";
import { cn } from "@/utils/cn";

type MicroOptionKey = "start" | "tools" | "time" | "stopped";

const targetByOption: Record<MicroOptionKey, string> = {
    start: "path-start",
    tools: "solutions",
    time: "access-plans",
    stopped: "when-needed",
};

type QuickStartFocusProps = {
    lang: Locale;
};

export function QuickStartFocus({ lang }: QuickStartFocusProps) {
    const copy = getLandingAddOnCopy(lang).micro;
    const [selected, setSelected] = useState<MicroOptionKey | null>(null);
    const clearTimerRef = useRef<number | null>(null);

    const selectedOption = useMemo(
        () => copy.options.find((option) => option.key === selected) ?? null,
        [copy.options, selected]
    );

    useEffect(() => {
        return () => {
            if (clearTimerRef.current !== null) {
                window.clearTimeout(clearTimerRef.current);
            }
        };
    }, []);

    function handleSelect(optionKey: MicroOptionKey) {
        setSelected(optionKey);

        const targetElement = document.getElementById(targetByOption[optionKey]);
        if (!targetElement) {
            return;
        }

        document
            .querySelectorAll<HTMLElement>(".focus-target--active")
            .forEach((el) => el.classList.remove("focus-target--active"));

        targetElement.classList.add("focus-target--active");

        const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        targetElement.scrollIntoView({
            behavior: prefersReducedMotion ? "auto" : "smooth",
            block: "start",
        });

        if (clearTimerRef.current !== null) {
            window.clearTimeout(clearTimerRef.current);
        }

        clearTimerRef.current = window.setTimeout(() => {
            targetElement.classList.remove("focus-target--active");
        }, 2800);
    }

    return (
        <section className="border-b border-[#d9cfbe] bg-[#f7f2e8] py-14 md:py-16" id="start-selector">
            <div className="container max-w-4xl">
                <div className="mb-8 text-center">
                    <h2 className="text-2xl font-bold text-[#1b2220] md:text-3xl">{copy.question}</h2>
                    <p className="mt-3 text-sm text-[#475953] md:text-base">{copy.helper}</p>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                    {copy.options.map((option) => {
                        const isPrimary = option.key === "start";
                        const isSelected = selected === option.key;

                        return (
                            <button
                                key={option.key}
                                type="button"
                                onClick={() => handleSelect(option.key)}
                                aria-pressed={isSelected}
                                className={cn(
                                    "min-h-12 rounded-2xl border bg-[#fffdf8] px-5 py-4 text-left text-sm text-[#2f403b] transition-colors duration-200 hover:border-[#0f766e]/40 hover:bg-[#fffaf0] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0f766e]",
                                    isPrimary ? "border-[#0f766e]/35" : "border-[#d7cab3]",
                                    isSelected && "border-[#0f766e] bg-[#ecf8f5] text-[#13423b]"
                                )}
                            >
                                <span className="flex items-center justify-between gap-3">
                                    <span className="font-medium">{option.label}</span>
                                    {isPrimary ? (
                                        <span className="rounded-full border border-[#0f766e]/30 bg-[#ecf8f5] px-2.5 py-1 text-[0.65rem] font-bold uppercase tracking-[0.11em] text-[#0f766e]">
                                            {copy.primaryTag}
                                        </span>
                                    ) : null}
                                </span>
                            </button>
                        );
                    })}
                </div>

                <div
                    aria-live="polite"
                    className={cn(
                        "mt-6 rounded-2xl border px-5 py-4 transition-colors duration-200",
                        selectedOption
                            ? "border-[#0f766e]/35 bg-[#ecf8f5]"
                            : "border-[#d7cab3] bg-[#fffdf8]"
                    )}
                >
                    {selectedOption ? (
                        <div className="flex items-start gap-3">
                            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#0f766e]" />
                            <div>
                                <p className="text-sm font-semibold text-[#13423b]">{copy.startPointLabel}</p>
                                <p className="mt-1 text-sm text-[#2f4c45]">{selectedOption.followUp}</p>
                            </div>
                        </div>
                    ) : (
                        <p className="text-sm text-[#50615b]">{copy.helper}</p>
                    )}
                </div>
            </div>
        </section>
    );
}
